import type { ProjectDetailAlertDto, SurveyDto, InstallationDto } from '../../../../lib/api/v2/planning'
import { deriveSectionStatus, type SectionTone } from './deriveSectionStatus'
import type { CommandesPhase } from './supplier-orders-phase'

/** One cell of the header status line (e.g. « Métré: Effectué »). */
export interface ProjectSummaryCell {
  key: 'metre' | 'pose' | 'commandes'
  title: string
  label: string
  tone: SectionTone
}

/**
 * Commandes tone/label for the header one-liner. Alerts win (they're the actionable signal); otherwise
 * the label follows the section PHASE so a freshly-signed project reads « À définir », not « En cours ».
 */
export function commandesSummary(alerts: ProjectDetailAlertDto[], phase: CommandesPhase): { tone: SectionTone; label: string } {
  if (alerts.some((a) => a.severity === 'Hard')) return { tone: 'rouge', label: 'À régler' }
  if (alerts.some((a) => a.severity === 'Soft')) return { tone: 'ambre', label: 'À vérifier' }
  switch (phase) {
    case 'recu': return { tone: 'vert', label: 'Reçues' }
    case 'rien': return { tone: 'gris', label: 'Rien à commander' }
    case 'a-definir': return { tone: 'gris', label: 'À définir' }
    default: return { tone: 'gris', label: 'En cours' }
  }
}

/** Always-worded section label (the header line shows a word per section, never a blank). */
function sectionLabel(kind: 'survey' | 'installation', survey: SurveyDto | InstallationDto, alerts: ProjectDetailAlertDto[], eventPast: boolean, hasUpcomingSlot: boolean): { tone: SectionTone; label: string } {
  const derived = deriveSectionStatus({
    status: survey.status, alerts, ready: survey.readyCheck.ready, missing: survey.readyCheck.missing,
    hasEvent: survey.currentEventId != null, eventPast, hasReport: survey.reportSummary != null,
    hasUpcomingSlot, kind,
  })
  if (derived.label) return { tone: derived.tone, label: derived.label }
  // deriveSectionStatus only returns null for Cancelled now (a terminal annulé) — word it for the one-liner.
  if (survey.status === 'Cancelled') return { tone: 'gris', label: 'Annulé' }
  return { tone: 'gris', label: '—' }
}

interface SummaryInput {
  survey: SurveyDto | undefined
  installation: InstallationDto | undefined
  metreAlerts: ProjectDetailAlertDto[]
  poseAlerts: ProjectDetailAlertDto[]
  commandesAlerts: ProjectDetailAlertDto[]
  commandesPhase: CommandesPhase
  /** Whether each work item's live RDV is past (drives the « À clôturer » header label, mini-scope 29). */
  surveyEventPast?: boolean
  installEventPast?: boolean
  /**
   * SOLY-PLAN-006 — un passage de métré encore à venir.
   *
   * 🔴 Trouvé par la QA browser sur staging, APRÈS le premier correctif : le bandeau appelle bien
   * `deriveSectionStatus` — une seule règle, c'est ce qui rend ça réparable — mais il ne lui passait
   * pas le drapeau, donc il retombait sur `false` et continuait d'annoncer « Métré: Effectué » à
   * cinq centimètres d'une section rouge « À régler · 2 points ». Un paramètre à valeur par défaut
   * est commode et c'est exactement par là que la deuxième surface s'est tue.
   */
  surveyHasUpcomingSlot?: boolean
}

/**
 * Project-level one-line status (cockpit UX finding #4): a compact, always-worded glance at the three
 * sections so the user grasps the whole project without scanning each card. Reuses deriveSectionStatus
 * for Métré/Pose and the Commandes alert-derived tone. Pure → unit-tested.
 */
export function deriveProjectSummary({
  survey, installation, metreAlerts, poseAlerts, commandesAlerts, commandesPhase,
  surveyEventPast = false, installEventPast = false, surveyHasUpcomingSlot = false,
}: SummaryInput): ProjectSummaryCell[] {
  const cells: ProjectSummaryCell[] = []
  if (survey) {
    const s = sectionLabel('survey', survey, metreAlerts, surveyEventPast, surveyHasUpcomingSlot)
    cells.push({ key: 'metre', title: 'Métré', ...s })
  }
  if (installation) {
    // La pose ne peut pas atteindre l'état (son rapport la ferme) — `false` en dur, pas un oubli.
    const p = sectionLabel('installation', installation, poseAlerts, installEventPast, false)
    cells.push({ key: 'pose', title: 'Pose', ...p })
  }
  const c = commandesSummary(commandesAlerts, commandesPhase)
  cells.push({ key: 'commandes', title: 'Commandes', ...c })
  return cells
}
